import styles from '@styles/screens/MapDashboardControlBoard.module.css';
import { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
// import DatePicker, { registerLocale } from "react-datepicker";
// import es from "date-fns/locale/es"; // the locale you want

import Map from '@components/Mapa/Map';
import { isEqual } from 'lodash';

import { ReactComponent as DayPickerSVG } from '@assets/SVGs/PresenteeismTable/DayPickerSVG.svg';
import { DatePickerPopper } from '@components/General/TablePoppers';
import { APIIntegration, apiConfig } from '@integrations/APIIntegration';
import { Navigate } from 'react-router-dom';
import Utils from '@utils/Utils';
import { Application } from '@core/Application';

const options = [
    { value: 'all', label: 'Todos' },
    { value: 'COMPLETED', label: 'Completadas' },
    { value: 'PENDING', label: 'Pendientes' },
    { value: 'GRAY', label: 'Sin turno' }
];

const customStylesStatus = {
    control: (styles) => ({
        ...styles,
        borderColor: '#F9F9F9',
        borderRadius: '10px',
        backgroundColor: '#F9F9F9',
        minHeight: '10px',
        height: '45px',
        width: '100%',
        boxShadow: 'none',
        "&:focus-within": {
          borderColor: "gray",
          boxShadow: "0 0 0.2rem rgba(233, 105, 71, 0.25)",
        }

    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
        ...styles,
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        backgroundColor: isSelected  ? '#EC6C56' : 'transparent',
        "&:hover": {
            backgroundColor: !isSelected ? '#ec6c5662' : 'EC6C56',
        }
    }),
    singleValue: (styles, state) => ({ 
        ...styles, 
        color: '#666',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',

        height: '50px',
        padding: '0 6px',
        paddingTop: '10px',
    }),
    input : (styles, state) => ({ 
        ...styles,
        color: 'transparent',
        margin: 0
    }),
    indicatorSeparator: state => ({
      display: 'none',
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      height: '45px',
    }),
}
  
function toISOLocal(d) {
    var z  = n =>  ('0' + n).slice(-2);
    var zz = n => ('00' + n).slice(-3);
    var off = d.getTimezoneOffset();
    var sign = off > 0? '-' : '+';
    off = Math.abs(off);
  
    return d.getFullYear() + '-'
           + z(d.getMonth()+1) + '-' +
           z(d.getDate()) + 'T' +
           z(d.getHours()) + ':'  + 
           z(d.getMinutes()) + ':' +
           z(d.getSeconds()) + '.' +
           zz(d.getMilliseconds()) +
           sign + z(off/60|0) + ':' + z(off%60); 
}

function ShiftInfoComponent({ shifts }) {

    const getColor = (cumplimiento) => {
        switch(cumplimiento) {
            case 'GREEN':
                return '#56A725'
            case 'RED':
                return '#FF5454'
            case 'GRAY':
                return '#949494'
            default: 
                return '#949494'
        }
    }

    return (
      <div style={{display: 'flex', flexDirection: 'column', paddingLeft: '5px', paddingTop: '5px'}}>
        {Object.entries(shifts).map(([shiftId, shiftDetails]) => (
          <div key={shiftId} style={{color: '#101828'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="3" fill={getColor(shiftDetails.color)}/>
            </svg>
            {" " + shiftDetails.shift.name}
          </div>
        ))}
      </div>
    );
  }

const MapDashboardControlBoard = ({org}) => {

    const [orgTimezone, setOrgTimezone] = useState(org.timezone ? org.timezone : 'America/Argentina/Buenos_Aires');

    const [dayString, setDayString] = useState(new Date().toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        timeZone: orgTimezone
    }).replace(/^(.*\sde\s)(.)(.*)$/, (match, p1, p2, p3) => p1 + p2.toUpperCase() + p3))

    const [selectedOption, setSelectedOption] = useState({ value: 'all', label: 'Todos' });


    const [markers, setMarkers] = useState([]);
    const [mapRef, setMapRef] = useState(null);
    const hasMounted = useRef(false);


    const [isLoading, setLoading] = useState(false);

    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [date, setDate] = useState(new Date());

    // Fetch markers on mount
    useEffect(() => {
        hasMounted.current = true;
        if (org.timezone) {
            setOrgTimezone(org.timezone)
        }
        console.log("[useEffect for org] Fetching markers on mount")
        fetchMarkers();
    }, [org]);

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        console.log("[useEffect for date] Fetching markers on date change")

        if (date) {
            setLoading(true);
            setDayString(date.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                timeZone: orgTimezone
            }).replace(/^(.*\sde\s)(.)(.*)$/, (match, p1, p2, p3) => p1 + p2.toUpperCase() + p3))
            fetchMarkers();
        } else {
            setDayString("Elige una fecha...")
            setMarkers([])
        }

    }, [date])


    useEffect(() => {
        console.log("[useEffect for markers] Changed markers: ", markers)
    },[markers])


    const handleClickDayPicker = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen((previousOpen) => !previousOpen);
    };

    const updateDate = (updatedDate) => {
        setDate(updatedDate)
    }


    const fetchMarkers = async () => {
        let processedDate = toISOLocal(date).split('T')[0]

        try {
            const dashboardService = Application.shared().coreDependencies.dashboardService;
            const result = await dashboardService.getPresenteeismShiftsDay(org.id, processedDate)
            let transformed = transformResponse(result)
            if(isEqual(markers, transformed)){
                console.log("[fetchMarkers] Current markers: ", markers, " same as new response: ", transformed)
                return
            }
            setMarkers(transformed)
            setSelectedOption(selectedOption)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false);
        }

    }


    const getStatusFromColors = (buildingShifts) => {
        let status = "GRAY"
        for (const shiftDetails of Object.values(buildingShifts)) {
            if (shiftDetails?.color === "RED") {
                return "PENDING"
            }
            if (shiftDetails?.color === "GREEN") {
                status = "COMPLETED"
            }
        }
        return status
    }

    const transformResponseV2 = (payload) => {
        let newMarkers = []
        for (const buildingId of Object.keys(payload.buildings || {})) {
            const buildingShifts = payload.buildings[buildingId].shifts || {};
            const shiftKeys = Object.keys(buildingShifts);
            if (shiftKeys.length === 0) {
                continue;
            }

            const firstShiftKey = shiftKeys[0]; // This grabs the first key name, which might be "T1", "T2", etc.
            const buildingInfo = buildingShifts[firstShiftKey]?.building;
            if (!buildingInfo || buildingInfo.disabled) {
                continue;
            }

            newMarkers.push({
                id: buildingInfo.id,
                title: buildingInfo.name,
                latitude: buildingInfo.latitude,
                longitude: buildingInfo.longitude,
                contents: () => <ShiftInfoComponent shifts={buildingShifts} />,
                status: getStatusFromColors(buildingShifts)
            })
        }
        return newMarkers
    }

    const transformResponse = (response) => {
        const payload = response?.result ? response.result : response;
        if (!payload || !payload.buildings) {
            return [];
        }

        if (payload.version === "V2") {
            return transformResponseV2(payload)
        }

        let newMarkers = []
        for (const buildingId of Object.keys(payload.buildings)) {

            const buildingShifts = payload.buildings[buildingId].shifts;

            const shiftKeys = Object.keys(buildingShifts);
            const firstShiftKey = shiftKeys[0]; // This grabs the first key name, which might be "T1", "T2", etc.
            const buildingInfo = buildingShifts[firstShiftKey].building; // Now access the shift object of the first shift
            if (buildingInfo.disabled) {
                continue;
            }
            
            let status = "GRAY"

            let possibleShifts = []
            for (const shiftKey of shiftKeys) {
                const shift = buildingShifts[shiftKey].shift;
                if (shift.hourFrom && shift.hourTo) {
                    if (Utils.isCurrentTimeBetween(shift.hourFrom, shift.hourTo)) {
                        possibleShifts.push(shift)
                    }
                }
            }

            for (const shift of possibleShifts) {
                if (buildingShifts[shift.id].color === "RED") {
                    status = "PENDING"
                    break;
                }

                if (buildingShifts[shift.id].color === "GREEN") {
                    status = "COMPLETED"
                }
            }

            newMarkers.push({
                id: buildingInfo.id,
                title: buildingInfo.name,
                latitude: buildingInfo.latitude,
                longitude: buildingInfo.longitude,
                contents: () => <ShiftInfoComponent shifts={buildingShifts} />,
                status: status
            })
        }
        return newMarkers
    }


    const handleChange = (selectedOption) => {
        setSelectedOption(selectedOption);
    }

    return(
        <div className={styles['content-block']}>
            <div className={styles['header-container']}>
                <div className={styles['title-container']}>
                    <h2 className={styles['title']}>
                        Mapa
                    </h2>
                    <p className={styles['sub-title']}>{dayString}</p>
                </div>


                <div className={styles['filters-container']}>
                    <div className={styles['daypicker']} onClick={handleClickDayPicker}>
                        <div className={styles['svg-container']} style={{marginRight: '8px'}}>
                            <DayPickerSVG/>
                        </div>
                        <p className={styles['item-text']}>Elegir Fecha</p>
                        {open && <DatePickerPopper
                            currentPickedDate={date}
                            updateDate={updateDate} 
                            clickAwayHandler={() => {setOpen(false)}}
                            placement="bottom-end"
                            anchorElement={anchorEl} 
                            isOpen={open} 
                            />
                        }
                    </div>
                </div>
            </div>
            <div className={styles['content-container']}>
                <div className={`${styles['map-container']} ${styles['skeleton']}`}>
                    {markers && !isLoading ? 
                    <Map setMapRefParent={setMapRef} radius={'20px'} markers={markers.filter(marker => {
                        if(selectedOption.value === 'all') {
                            return true;
                        } else {
                            return marker.status === selectedOption.value;
                        }
                    })}/>
                    : 
                    <div style={{width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    </div>}
                </div>

                <div className={styles['map-sidebar-container']}>
                    <div style={{height: '5%', width: '95%', marginBottom: '15%'}}>
                        <Select
                            isSearchable={false}
                            defaultValue={selectedOption}
                            onChange={option => handleChange(option)}
                            options={options}
                            styles={customStylesStatus}
                            className='react-select-container'
                            classNamePrefix='react-select'
                        />
                    </div>

                    <div className={styles['map-sidebar-markers-container']} 
                        style={{overflowY: 'scroll', height: '100%', width: '100%', flexShrink: 0.8}} 
                    >
                        {markers.filter(marker => {
                                if(selectedOption.value === 'all'){
                                    return true;
                                } else {
                                    return marker.status === selectedOption.value;
                                }
                            }).map((marker) => {
                                return(
                                    <div className={styles['map-sidebar-item-container']} key={marker.id}>
                                        <p className={styles['map-sidebar-item-text']} onClick={() => {
                                            if (mapRef) {
                                                mapRef.panTo({lat: marker.latitude, lng: marker.longitude});
                                                mapRef.setZoom(14); 
                                            }
                                        }}>
                                            {marker.title}
                                        </p>
                                    </div>
                                )
                        })}
                        {markers.length === 0 && <p className={styles['map-sidebar-item-text']} style={{fontSize: '0.55em'}}>Aun no hay marcadores!</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

const MapDashboardControlBoardWrapper = ({org}) => {
    if (!org.newFlags.WebDashboardMap) {
        return <Navigate to="/" />;
    }

    return <MapDashboardControlBoard org={org}/>
}

export default MapDashboardControlBoardWrapper
